import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity } from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { Logger } from '../utils/logger';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { WebPageShell } from '../components/common/WebPageShell';
import { ActivityItem } from '../utils/notificationMapper';
import { formatRelativeTime } from '../utils/dateUtils';
import { useParentFeed } from '../hooks/useParentFeed';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { useWindowDimensions } from 'react-native';

export function NotificationsScreen() {
  const { user } = useAuth();
  const navigation = useNavigation<any>();
  const { resetUnreadCount } = useNotifications();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  // The persisted feed merged with live messages; isNew mirrors ReadAt.
  const { activities, isLoading, markRead, acknowledge, activeEmergency } = useParentFeed();

  useEffect(() => {
    resetUnreadCount();
  }, [resetUnreadCount]);

  const handleNotificationPress = (item: ActivityItem) => {
    resetUnreadCount();
    markRead(item);

    switch (item.badge.text) {
      case 'Attendance':
        navigation.navigate('Attendance');
        break;
      case 'Homework':
        navigation.navigate('Academics');
        break;
      case 'Payment':
      case 'Canteen':
        navigation.navigate('Fees');
        break;
      case 'Announcement':
        navigation.navigate('Communication', { initialTab: 'Alerts' });
        break;
      default:
        break;
    }
  };

  // REPLY_TO_TEACHER routes into the existing parent-teacher chat; the
  // teacher-send flow targets the class teacher, which is who that chat is with.
  const replyTo = (item: ActivityItem) => {
    markRead(item);
    navigation.navigate('Chat', { replyToSubject: item.name });
  };

  // Was a local copy of this diff arithmetic over a mis-parsed Date; the shared
  // helper reads the timestamp as the UTC it actually is (see dateUtils).
  const formatTime = (timestamp: string) => formatRelativeTime(timestamp);

  const renderItem = ({ item }: { item: ActivityItem }) => (
    <TouchableOpacity 
      style={[styles.card, item.isNew && styles.unreadCard]}
      onPress={() => handleNotificationPress(item)}
    >
      <View style={styles.cardContent}>
        <View style={[styles.icon, { backgroundColor: item.badge.color }]}>
          <FeatherIcon name="bell" size={16} color="#FFF" />
        </View>
        <View style={styles.textContent}>
          <Text style={[styles.title, item.isNew && styles.titleBold]}>
            {item.name}
          </Text>
          <Text style={styles.message} numberOfLines={2}>
            {item.details}
          </Text>
          <Text style={styles.time}>
            {formatTime(item.createdAt)}
          </Text>
          {(item.requiresAcknowledgement || item.replyMode === 'REPLY_TO_TEACHER') && (
            <View style={[styles.rowActions, isMobile && styles.rowActionsMobile]}>
              {item.requiresAcknowledgement && !item.acknowledgedAt && (
                <TouchableOpacity style={[styles.actionButton, styles.ackButton]}
                  onPress={() => acknowledge(item)}>
                  <Text style={[styles.actionButtonText, styles.ackButtonText]}>Acknowledge</Text>
                </TouchableOpacity>
              )}
              {item.replyMode === 'REPLY_TO_TEACHER' && (
                <TouchableOpacity style={styles.actionButton} onPress={() => replyTo(item)}>
                  <Text style={styles.actionButtonText}>Reply to teacher</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
          {item.requiresAcknowledgement && item.acknowledgedAt && (
            <Text style={styles.ackedText}>✓ Acknowledged</Text>
          )}
        </View>
      </View>
      {item.isNew && <View style={styles.unreadIndicator} />}
    </TouchableOpacity>
  );

  return (
    <WebPageShell title="Notifications" contentStyle={{ maxWidth: 800 }}>
      {activeEmergency && (
        <TouchableOpacity style={styles.emergencyBanner}
          onPress={() => acknowledge(activeEmergency)}>
          <Text style={styles.emergencyTitle}>🚨 EMERGENCY: {activeEmergency.name}</Text>
          <Text style={styles.emergencyBody} numberOfLines={3}>{activeEmergency.details}</Text>
          <Text style={styles.emergencyAction}>Tap to acknowledge</Text>
        </TouchableOpacity>
      )}
      {isLoading ? (
        <LoadingSpinner message="Loading notifications..." />
      ) : activities.length === 0 ? (
        <View style={styles.empty}>
          <FeatherIcon name="bell-off" size={48} color={colors.textMuted} />
          <Text style={styles.emptyText}>No notifications</Text>
        </View>
      ) : (
        <FlatList
          data={activities}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          scrollEnabled
          ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
        />
      )}
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  header: { fontSize: 22, fontWeight: '800', color: colors.textPrimary, marginBottom: 16 },
  emergencyBanner: { backgroundColor: '#DC2626', borderRadius: 12, padding: 16, marginBottom: 12 },
  emergencyTitle: { color: '#FFF', fontWeight: '800', fontSize: 15, marginBottom: 4 },
  emergencyBody: { color: '#FEE2E2', fontSize: 13, lineHeight: 18, marginBottom: 6 },
  emergencyAction: { color: '#FFF', fontWeight: '700', fontSize: 12, textDecorationLine: 'underline' },
  rowActions: { flexDirection: 'row', gap: 10, marginTop: 8 },
  rowActionsMobile: { flexWrap: 'wrap' },
  actionButton: { backgroundColor: colors.primaryLight, borderRadius: 8, paddingVertical: 6, paddingHorizontal: 12 },
  actionButtonText: { color: colors.primary, fontWeight: '700', fontSize: 12 },
  ackButton: { backgroundColor: '#FEF3C7' },
  ackButtonText: { color: '#B45309' },
  ackedText: { color: '#16A34A', fontSize: 12, fontWeight: '600', marginTop: 6 },

  loader: { marginTop: 40 },
  empty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
  emptyText: { fontSize: 16, color: colors.textMuted, marginTop: 12, fontWeight: '600' },
  card: { backgroundColor: colors.card, borderRadius: 12, padding: 16, elevation: 1, shadowColor: colors.shadow, shadowOpacity: 0.04, shadowRadius: 6, shadowOffset: { width: 0, height: 2 }, flexDirection: 'row', alignItems: 'center' },
  unreadCard: { backgroundColor: colors.primaryLight, borderLeftWidth: 4, borderLeftColor: colors.primary },
  cardContent: { flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  icon: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  textContent: { flex: 1 },
  title: { fontSize: 14, color: colors.textSecondary, marginBottom: 4 },
  titleBold: { fontWeight: '700', color: colors.textPrimary },
  message: { fontSize: 13, color: colors.textSecondary, lineHeight: 18, marginBottom: 4 },
  time: { fontSize: 11, color: colors.textMuted },
  unreadIndicator: { width: 10, height: 10, borderRadius: 5, backgroundColor: colors.primary, marginLeft: 12 },
});
